<template>
  <Modal
    v-model="open"
    class="modal--xlg modal--align-top"
    @input="$emit('closeModal')"
  >
    <template #modal-panel>
      <div class="advanced-search-modal">
        <header class="modal-panel__header">
          <h1 class="modal__title">
            {{ $t("advancedsearch.criteria.modal.title") }}
          </h1>
          <p>{{ $t("advancedsearch.criteria.modal.subtitle") }}</p>
        </header>
        <div class="modal-panel__content">
          <div class="f-field-search">
            <button
              :aria-label="$t('search')"
              class="f-field-search__submit"
            />
            <input
              ref="criteriaSearch"
              v-model.trim="criteriaQuery"
              :placeholder="$t('advancedsearch.criteria.modal.bar')"
              type="search"
              class="f-field-search__input f-field-search__input--card"
            >
          </div>
          <p class="advanced-search-modal__repeat-text u-text-blue small">
            {{ $t('advancedsearch.criteria.in.use') }}
          </p>
          <section class="advanced-search-modal__criteria-panel">
            <template
              v-if="hasMatchingParsers"
            >
              <section class="advanced-search-modal__criteria-listing">
                <div
                  v-for="field in filteredParsers"
                  :key="field.key"
                >
                  <button
                    class="advanced-search-modal__criteria-list-item"
                    @click="addField(field.parserName, field.key)"
                  >
                    <!-- eslint-disable vue/no-v-html -->
                    <span
                      :class="{ 'advanced-search-modal__criteria': true, 'criteria-is-repeated': field.isSelected }"
                      v-html="renderField(field, matchStart='<mark>', matchEnd='</mark>')"
                    />
                  <!-- eslint-enable -->
                  </button>
                </div>
              </section>
            </template>
            <template v-else>
              <RecordEmptyState>
                <template #title>
                  {{ $t("advancedsearch.criteria.search.empty.state.title") }}
                </template>
                <template #description>
                  {{ $t("advancedsearch.criteria.search.empty.state.description") }}
                </template>
              </RecordEmptyState>
            </template>
          </section>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script>

import RecordSearchFieldsHelpers from '#src/mixins/RecordSearchFieldsHelpers'
import SearchExpressionHelpers from '#src/mixins/SearchExpressionHelpers'
import RecordEmptyState from '#src/components/empty-states/Record.vue'
import Modal from '#src/components/utils/Modal.vue'
import { searchFilter, renderMatchResults } from '#src/utils/search-helpers'

export default {
  components: {
    Modal,
    RecordEmptyState
  },
  mixins: [RecordSearchFieldsHelpers, SearchExpressionHelpers],
  props: {
    showModal: {
      type: Boolean,
      required: true
    },
    parsers: {
      type: Object,
      required: true
    },
    fields: {
      type: Array,
      required: true
    }
  },
  emits: ['closeModal', 'addField'],
  data() {
    return {
      criteriaQuery: '',
      open: false
    }
  },
  computed: {
    allFields: function () {
      return Array.from(new Set(
        Object.values(this.parsers).flat()
      ))
    },
    filteredParsers: function () {
      const allFields = new Set()
      const parsers = Object.keys(this.parsers).reduce((acc, parserName) => {
        const selectedFields = this.fields.map(selected => ({ name: selected.name, parserName: selected.parserName }))
        const fields = searchFilter(
          this.criteriaQuery,
          this.parsers[parserName],
          f => this.$t(`fields.record.${this.getFieldLabelName(f)}.label`)
        ).map((f) => {
          const isSelected = selectedFields.some(selected => selected.name === f.key)
          return { ...f, isSelected }
        }).filter(f => !allFields.has(f.key))
        for (const field of fields) {
          allFields.add(field.key)
        }
        return [...acc, ...fields.map(f => ({ parserName, ...f }))]
      }, [])
      return parsers.sort((a, b) => a.value.localeCompare(b.value))
    },
    hasMatchingParsers: function () {
      return Object.keys(this.filteredParsers).length > 0
    }
  },
  watch: {
    // can't use v-bind.sync because of three-way binding and mutating props directly
    showModal: async function (value) {
      if (value === true) {
        this.open = true
        await this.$nextTick()
        this.$refs.criteriaSearch?.focus()
      } else {
        this.open = false
      }
    }
  },
  methods: {
    renderField(field) {
      return renderMatchResults(field, '<mark>', '</mark>')
    },
    addField(parserName, fieldName) {
      this.$emit('addField', { parserName, fieldName })
      this.$emit('closeModal')
    }
  }
}
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "#src/assets/scss/variables" as *;
@use "#src/assets/scss/icons" as *;

span {
  color: $slate;
}

.advanced-search-modal {
  @mixin criteria-asterisk {
    content: "*";
    font-weight: 600;
  }

  .advanced-search-modal__repeat-text {
    margin-bottom: 3.5rem;
    &::before {
      @include criteria-asterisk;
    }
  }

  .criteria-is-repeated {
    &::after {
      @include criteria-asterisk;

      color: $blue-600;
      margin-left: 0.25rem;
    }
  }

  .advanced-search-modal__criteria-listing {
    column-count: 1;
    column-gap: 4rem;

    @media screen and (width >= 32.5rem) {
      column-count: 2;
    }

    @media screen and (width >= 60rem) {
      column-count: 3;
    }
  }

  .advanced-search-modal__criteria-list-title {
    padding-left: 0.5rem;
  }

  .advanced-search-modal__criteria-list-item {
    position: relative;
    padding: 0.375rem 0.5rem;
    width: 100%;
    cursor: pointer;
    @include md-transition;

    &:hover,
    &:focus {
      box-shadow: 0 0.1875rem 0.4375rem 0 color.change(black, $alpha: 0.1);
    }

    &::after {
      content: "";
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 0.25rem;

      @include circle-add-icon-structure;
    }

    &:hover::after,
    &:focus::after {
      @include circle-add-icon("009DE0");
    }
  }

  .advanced-search-modal__criteria {
    padding-right: 2rem;
  }
}
</style>
